/**
 * İmza/UYAP giriş hatalarının makine tarafından okunur kodları (DEV-3898, DEV-3901).
 *
 * Sessiz (arka plan) giriş kullanıcıya pencere açmadan nedenini kodla bildirir; bot
 * döngüsü/imzacı kodu okuyup "bekleme sebebi" gösterir. Kod, Error nesnesinin `kod`
 * alanındadır: `(e as any).kod`.
 */

export type ImzaHataKodu = 'PIN_GEREKLI' | 'PIN_KILITLI' | 'KART_YOK' | 'IMZA_PROGRAMI_KAPALI' | 'TCKN_EKSIK';

export const IMZA_HATA_KODLARI: ImzaHataKodu[] = ['PIN_GEREKLI', 'PIN_KILITLI', 'KART_YOK', 'IMZA_PROGRAMI_KAPALI', 'TCKN_EKSIK'];

export const KART_YOK_MESAJI = 'Takılı akıllı kart bulunamadı.';
export const IMZA_PROGRAMI_KAPALI_MESAJI =
    'E-İmza Programı açık değil veya hata oluştu, lütfen e-İmza Programını açınız.';
export const PIN_GEREKLI_MESAJI =
    'Kartın PIN kodu kayıtlı değil veya otomatik oturum açma kapalı. UYAP girişi için "Bağlan"a basıp PIN kodunuzu girin.';
export const PIN_YANLIS_MESAJI =
    'PIN kodu yanlış. "Bağlan"a basıp doğru PIN kodunu girin. Art arda yanlış denemeler e-imza kartını kilitler.';
/** Hata mesajına, silinecek kayıtlı PIN gerçekten varsa eklenir. */
export const KAYITLI_PIN_SILINDI_EKI = ' Kayıtlı PIN silindi.';
export const PIN_KILITLI_MESAJI =
    'E-imza kartınızın PIN kodu kilitlenmiş. Kartın kilidini, kartı aldığınız e-imza sağlayıcısının programında PUK koduyla açmanız gerekir; kilit açılmadan UYAP girişi yapılamaz.';
export const HESAP_TCKN_EKSIK_MESAJI =
    'Bu büronun eİcraPro lisansında TC kimlik numarası kayıtlı değil. UYAP girişi için destek ile iletişime geçin.';
export const SERTIFIKA_TCKN_EKSIK_MESAJI =
    'E-imza kartındaki TC kimlik numarası okunamadı. Doğru kartın takılı olduğundan emin olun.';

export function kodluHata(mesaj: string, kod: ImzaHataKodu): Error {
    const hata: any = new Error(mesaj);
    hata.kod = kod;
    return hata;
}

/** Hatadaki kodu döndürür; kodsuz/bilinmeyen hatada null. */
export function imzaHataKodu(hata: any): ImzaHataKodu | null {
    const kod = hata ? hata.kod : undefined;
    return IMZA_HATA_KODLARI.indexOf(kod) >= 0 ? kod : null;
}

/**
 * İmzalama programının (localhost:5975, kabukta :6075) `/api/v1/signature/sign` cevabının sonucu.
 *  - tamam: imza alındı.
 *  - pin-yanlis: program PIN'in yanlış olduğunu söyledi.
 *  - pin-kilitli: kartın PIN'i kilitli (yanlış denemeler tükendi; PUK gerekir).
 *  - hata: başka bir imza hatası (kart iletişimi vb.); PIN'le ilgisi olup olmadığı bilinmez.
 */
export type ImzaYanitSonucu =
    | {durum: 'tamam', signedData: string, txId: string}
    | {durum: 'pin-yanlis' | 'pin-kilitli' | 'hata', mesaj: string};

/**
 * Sign cevabının gövdesini sınıflandırır. Program hatayı HTTP 200 ile gövdede döner, bu yüzden HTTP
 * durumuna değil gövdeye bakılır. UYAP'tan alınan gerçek cevaplar (19.10.2026 HAR'ları, hepsi HTTP 200):
 *  - doğru PIN:  {"data":{"signedData":"…","txId":"…","signedECorrespondenceData":null},
 *                 "metadata":{"STATUS":"SUCCESS","MESSAGE":"İşlem başarıyla gerçekleştirildi!"}}
 *  - yanlış PIN: {"data":null,"metadata":{"STATUS":"ERROR","MESSAGE":"Hatalı PIN."}}
 *  - kilitli:    {"data":null,"metadata":{"STATUS":"ERROR","MESSAGE":"PIN kilitli."}} Türkçe büyük harfler (İ, I) küçültülünce
 * birleştirici nokta ve i/ı farkı çıkar; desenler ikisini de kabul eder.
 */
export function imzaYanitiniSiniflandir(govde: any): ImzaYanitSonucu {
    const signedData = govde?.data?.signedData;
    const txId = govde?.data?.txId;
    if (signedData && txId) {
        return {durum: 'tamam', signedData, txId};
    }
    const durum = String(govde?.metadata?.STATUS ?? '');
    const mesaj = String(govde?.metadata?.MESSAGE ?? '').trim();
    const metin = (durum + ' ' + mesaj).toLowerCase().replace(/\u0307/g, '');
    // Kilit: PIN'in KİLİTLİ olduğu (şimdiki/geçmiş zaman) söylenmeli. "Kart kilitlenebilir/kilitlenir"
    // gibi uyarılar yanlış PIN mesajının parçasıdır; kart okuyucu kilidi PIN kilidi değildir.
    if (/pin_?(locked|blocked)/.test(metin)
        || /p[iı]n(\s+\S+)?\s+(kilitli|kilitlendi|kilitlenmi|bloke|locked|blocked)/.test(metin)
        || /(kilitli|locked|blocked)\s+p[iı]n/.test(metin)) {
        return {durum: 'pin-kilitli', mesaj};
    }
    if (durum === 'WRONG_PIN'
        || /pin_?incorrect/.test(metin)
        || (/p[iı]n|şifre|sifre|parola|password/.test(metin)
            && /yanl[ıi][şs]|hatal[ıi]|geçersiz|gecersiz|incorrect|wrong|invalid/.test(metin))) {
        return {durum: 'pin-yanlis', mesaj};
    }
    return {durum: 'hata', mesaj};
}
